import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import metadata from "../../metadata.json";
import apiClient from "../../api/apiClient";

import {
  editProfileRequest,
  changePasswordRequest,
  clearProfileMessages,
} from "../../redux/actions/profileActions";
import { getOemPartnerRequest } from "../../redux/actions/oemActions";
import { BiEdit } from "react-icons/bi";
import { MdVerified } from "react-icons/md";
import { updateLogoRequest } from "../../redux/actions/logoActions";
import ChangeMailModal from "../../components/modal/ChangeMailModal";
import "../../styles/ReactSelect.css";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaBuilding,
  FaGlobe,
  FaDollarSign,
  FaChartBar,
  FaCalendar,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { FaCamera } from "react-icons/fa";

// Static country data
const STATIC_COUNTRY_DATA = [
  { name: "United States", code: "US", dialCode: "+1", currency: "USD" },
  { name: "Canada", code: "CA", dialCode: "+1", currency: "CAD" },
  { name: "United Kingdom", code: "GB", dialCode: "+44", currency: "GBP" },
  { name: "Germany", code: "DE", dialCode: "+49", currency: "EUR" },
  { name: "France", code: "FR", dialCode: "+33", currency: "EUR" },
  { name: "Australia", code: "AU", dialCode: "+61", currency: "AUD" },
  { name: "India", code: "IN", dialCode: "+91", currency: "INR" },
  { name: "Japan", code: "JP", dialCode: "+81", currency: "JPY" },
  { name: "Brazil", code: "BR", dialCode: "+55", currency: "BRL" },
  { name: "South Africa", code: "ZA", dialCode: "+27", currency: "ZAR" },
];

const Profile = () => {
  const { user } = useSelector((state) => state.auth);

  // Profile form with default values
  const {
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    register: registerProfile,
    setValue: setProfileValue,
    control: controlProfile,
    watch: watchProfile,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      currency: "",
      country: "",
      contactNumber: "",
      buttonColor: "#737EE2",
      backgroundColor: "#FFFFFF",
    },
  });

  const id = user?.oemPartner?.id;

  // Password form
  const {
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    register: registerPassword,
    watch: watchPassword,
  } = useForm({});

  const newPassword = watchPassword("newPassword");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();
  const {
    loading: profileLoading,
    error,
    success,
  } = useSelector((state) => state.profile);
  const { loading: oemLoading, data: profileData } = useSelector(
    (state) => state.oem,
  );

  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [countries, setCountries] = useState(STATIC_COUNTRY_DATA);
  const [countryCodes, setCountryCodes] = useState(STATIC_COUNTRY_DATA);
  console.log("profileData in profile page:", profileData);
  const [countryCodeMap, setCountryCodeMap] = useState({});
  const [selectedCountryCode, setSelectedCountryCode] = useState("US");
  const [flagLink, setFlagLink] = useState("us");
  const [countryCode, setCountryCode] = useState("+1");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const profilePicInputRef = React.useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dbVersion, setDbVersion] = useState(null);
  background: watchProfile("backgroundColor");
  backgroundColor: watchProfile("buttonColor");

  useEffect(() => {
    apiClient.get("/versions/latest")
      .then((res) => {
        if (res.data?.version) setDbVersion(res.data.version);
      })
      .catch(() => {});
  }, []);

  const handlePreviewClick = () => {
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  const previewCompanyName =
    profileData?.companyName || user?.oemPartner?.companyName || "Your Company";

  // Fetch profile data
  useEffect(() => {
    if (id && user) {
      dispatch(getOemPartnerRequest(id));
    }
  }, [dispatch, id, user]);

  // Set up country code mapping
  useEffect(() => {
    const codeToName = {};
    STATIC_COUNTRY_DATA.forEach((country) => {
      codeToName[country.code] = country.name;
    });
    setCountryCodeMap(codeToName);
  }, []);

  // Set form values when profile data loads - THIS IS THE KEY FIX
  useEffect(() => {
    if (profileData && !isDataLoaded) {
      // Set basic fields
      setProfileValue("firstName", profileData.firstName || "");
      setProfileValue("lastName", profileData.lastName || "");
      setProfileValue("currency", profileData.currency || "USD");
      setProfileValue("buttonColor", profileData.buttonColor || "#737EE2");
      setProfileValue(
        "backgroundColor",
        profileData.backgroundColor || "#FFFFFF",
      );

      // Set country
      if (profileData.country) {
        setProfileValue("country", profileData.country);

        // Find the country data for flag and code
        const countryInfo = STATIC_COUNTRY_DATA.find(
          (c) => c.name === profileData.country,
        );
        if (countryInfo) {
          setSelectedCountryCode(countryInfo.code);
          setFlagLink(countryInfo.code.toLowerCase());
          setCountryCode(countryInfo.dialCode);
        }
      }

      // Set contact number (remove country code if present)
      if (profileData.contactNumber) {
        let cleanNumber = profileData.contactNumber;
        // Remove country code if it exists in the number
        if (
          profileData.countryCode &&
          cleanNumber.startsWith(profileData.countryCode)
        ) {
          cleanNumber = cleanNumber.replace(profileData.countryCode, "");
        } else if (cleanNumber.startsWith("+1")) {
          cleanNumber = cleanNumber.replace("+1", "");
        } else if (cleanNumber.match(/^\+\d{1,3}/)) {
          cleanNumber = cleanNumber.replace(/^\+\d{1,3}/, "");
        }
        setProfileValue("contactNumber", cleanNumber);
      }

      setIsDataLoaded(true);
    }
  }, [profileData, setProfileValue, isDataLoaded]);

  const onProfileSubmit = (data) => {
    setIsProfileSubmitting(true);
    const fullContactNumber = data.contactNumber
      ? `${countryCode}${data.contactNumber}`
      : "";

    const profileDataToSubmit = {
      oemId: id,
      firstName: data.firstName,
      lastName: data.lastName || "",
      country: data.country,
      currency: data.currency,
      contactNumber: fullContactNumber,
      buttonColor: data.buttonColor,
      backgroundColor: data.backgroundColor,
    };

    dispatch(editProfileRequest(profileDataToSubmit));
  };

  const onPasswordSubmit = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      return;
    }

    setIsPasswordSubmitting(true);
    const passwordData = {
      oemId: id,
      oldPassword: data.currentPassword,
      newPassword: data.newPassword,
    };
    dispatch(changePasswordRequest(passwordData));
  };

  useEffect(() => {
    if (success) {
      setIsProfileSubmitting(false);
      setIsPasswordSubmitting(false);
      dispatch(clearProfileMessages());
    }
    if (error) {
      setIsProfileSubmitting(false);
      setIsPasswordSubmitting(false);
      dispatch(clearProfileMessages());
    }
  }, [success, error, dispatch]);

  const loadCountryOptions = async (inputValue) => {
    const filtered = STATIC_COUNTRY_DATA.filter((country) =>
      country.name.toLowerCase().includes(inputValue?.toLowerCase() || ""),
    )
      .slice(0, 10)
      .map((country) => ({
        value: country.name,
        label: country.name,
        isoCode: country.code,
        currency: country.currency || "USD",
        countryCode: country.dialCode,
      }));

    return filtered;
  };
  const lightenColor = (color, percent = 80) => {
    const num = parseInt(color.replace("#", ""), 16);

    let r = (num >> 16) + Math.round(((255 - (num >> 16)) * percent) / 100);
    let g =
      ((num >> 8) & 0x00ff) +
      Math.round(((255 - ((num >> 8) & 0x00ff)) * percent) / 100);
    let b =
      (num & 0x0000ff) + Math.round(((255 - (num & 0x0000ff)) * percent) / 100);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleCountryChange = (selectedOption, { action }) => {
    if (action === "clear") {
      setProfileValue("country", "");
      setProfileValue("currency", "");
      setFlagLink("us");
      setCountryCode("+1");
      setSelectedCountryCode("US");
      return null;
    }
    setProfileValue("country", selectedOption?.value || "");
    setProfileValue("currency", selectedOption?.currency || "USD");
    setFlagLink(selectedOption?.isoCode?.toLowerCase() || "us");
    setCountryCode(selectedOption?.countryCode || "+1");
    setSelectedCountryCode(selectedOption?.isoCode || "US");
    return selectedOption;
  };

  if (oemLoading && !profileData) {
    return (
      <div className="page-container">
        <div className="page-card scroll-container">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100vh",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "50px",
                height: "50px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginBottom: "20px",
              }}
            ></div>
            <p style={{ color: "#7f8c8d", fontSize: "16px" }}>
              Loading profile...
            </p>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-2">
        <div className="scroll-container">
          <div className="input-form-section">
            <div className="row g-2">
              {/* Left Side - Profile Details Display */}
              <div className="col-3">
                <div className="page-card1 h-100 p-4">
                  {/* Profile Picture & Basic Info */}
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: "30px",
                      position: "relative",
                      display: "inline-block",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        margin: "0 auto",
                      }}
                    >
                      {profileData?.logo ? (
                        <img
                          src={profileData.logo}
                          alt="Profile"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            objectFit: "scale-down",
                            border: "#737EE2",
                            background:
                              "radial-gradient(circle, rgb(227, 242, 253), black)",
                          }}
                        />
                      ) : profileData?.firstName?.[0] ||
                        profileData?.lastName?.[0] ? (
                        <div
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, rgb(37, 99, 235), rgb(67, 56, 202))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 15px",
                            fontSize: "32px",
                            color: "#fff",
                          }}
                        >
                          {profileData?.firstName?.[0]}
                          {profileData?.lastName?.[0]}
                        </div>
                      ) : (
                        <img
                          src="https://via.placeholder.com/80x80/2563eb/ffffff?text=👤"
                          alt="Default Profile"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid rgb(37, 99, 235)",
                            background: "#f9fafb",
                          }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => profilePicInputRef.current?.click()}
                        style={{
                          position: "absolute",
                          bottom: "0",
                          right: "0",
                          background: "#737EE2",
                          color: "white",
                          border: "2px solid white",
                          borderRadius: "50%",
                          width: "28px",
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "14px",
                          padding: "0",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <FaCamera />
                      </button>
                      <input
                        type="file"
                        ref={profilePicInputRef}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          dispatch(
                            updateLogoRequest({ file, _id: profileData?._id }),
                          );
                        }}
                        accept="image/*"
                        style={{ display: "none" }}
                      />
                    </div>
                    <h4
                      style={{
                        margin: "0",
                        color: "#1f2937",
                        fontWeight: "600",
                      }}
                    >
                      {profileData?.firstName} {profileData?.lastName}
                    </h4>
                    <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>
                      {profileData?.companyName}
                    </p>
                  </div>

                  {/* About Section */}
                  <div style={{ marginBottom: "25px" }}>
                    <h6
                      style={{
                        color: "#1f2937",
                        marginBottom: "15px",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        borderBottom: "1px solid #f3f4f6",
                        paddingBottom: "10px",
                      }}
                    >
                      About
                    </h6>
                    <div style={{ marginBottom: "10px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaPhoneAlt style={{ marginRight: "5px" }} /> Phone:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.contactNumber || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "10px", position: "relative" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaEnvelope style={{ marginRight: "5px" }} /> Email:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.email || "N/A"}
                      </span>
                      <span
                        className="position-absolute"
                        style={{
                          top: "-12%",
                          fontSize: "18px",
                          color: "#10b981",
                          cursor: "pointer",
                        }}
                        title="Email Verified"
                      >
                        <MdVerified />
                      </span>
                    </div>
                  </div>

                  {/* Company Details */}
                  <div style={{ marginBottom: "25px" }}>
                    <h6
                      style={{
                        color: "#1f2937",
                        marginBottom: "15px",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        borderBottom: "1px solid #f3f4f6",
                        paddingBottom: "10px",
                      }}
                    >
                      Company Details
                    </h6>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaBuilding style={{ marginRight: "5px" }} /> Company:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.companyName || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaGlobe style={{ marginRight: "5px" }} /> Country:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.country || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaDollarSign style={{ marginRight: "5px" }} />{" "}
                        Currency:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.currency || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaChartBar style={{ marginRight: "5px" }} /> Almata
                        Share:
                      </span>
                      <span
                        className="ms-2"
                        style={{ fontSize: "0.9rem", color: "#374151" }}
                      >
                        {(() => {
                          const description =
                            profileData?.almataSharedDiscription || "N/A";
                          if (description === "N/A") return description;
                          const words = description.split(" ");
                          const isLong = words.length > 25;
                          const truncated = words.slice(0, 25).join(" ");
                          return (
                            <>
                              {isLong ? truncated + "..." : description}
                              {isLong && (
                                <span
                                  style={{
                                    color: "rgb(37, 99, 235)",
                                    cursor: "pointer",
                                    marginLeft: "5px",
                                    fontWeight: "500",
                                  }}
                                  onClick={() => setShowDescriptionModal(true)}
                                >
                                  more
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaCalendar style={{ marginRight: "5px" }} /> Created:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.createdAt
                          ? new Date(profileData.createdAt).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Edit Forms */}
              <div className="col-9" style={{ paddingLeft: "10px" }}>
                <div className="page-card1 p-4 mb-3">
                  <div>
                    <h6
                      style={{
                        borderBottom: "1px solid #ddd",
                        paddingBottom: "10px",
                        marginBottom: "20px",
                      }}
                    >
                      Edit Profile Information
                    </h6>
                    <form
                      onSubmit={handleProfileSubmit(onProfileSubmit)}
                      className="input-form"
                    >
                      <div className="row">
                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>
                              First Name <span className="text-danger">*</span>
                            </label>
                            <input
                              {...registerProfile("firstName", {
                                required: "This field is required",
                              })}
                              className="form-input"
                              placeholder="Enter first name"
                            />
                            {profileErrors.firstName && (
                              <span className="error-text">
                                {profileErrors.firstName.message}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>Last Name</label>
                            <input
                              {...registerProfile("lastName")}
                              className="form-input"
                              placeholder="Enter last name"
                            />
                            {profileErrors.lastName && (
                              <span className="error-text">
                                {profileErrors.lastName.message}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>Currency</label>
                            <input
                              {...registerProfile("currency")}
                              className="form-input"
                              placeholder="Currency will be set automatically"
                              readOnly
                              style={{
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>
                              Country <span className="text-danger">*</span>
                            </label>
                            <Controller
                              name="country"
                              control={controlProfile}
                              rules={{ required: "Country is required" }}
                              render={({ field: { onChange, value } }) => (
                                <AsyncSelect
                                  value={
                                    value
                                      ? { value: value, label: value }
                                      : null
                                  }
                                  loadOptions={loadCountryOptions}
                                  onChange={(selectedOption, action) => {
                                    if (selectedOption) {
                                      handleCountryChange(
                                        selectedOption,
                                        action,
                                      );
                                      onChange(selectedOption.value || "");
                                    } else {
                                      handleCountryChange(null, action);
                                      onChange("");
                                    }
                                  }}
                                  placeholder="Search country..."
                                  className={
                                    profileErrors.country ? "error" : ""
                                  }
                                  classNamePrefix="react-select"
                                  isClearable
                                  defaultOptions
                                  noOptionsMessage={() => "No countries found"}
                                  loadingMessage={() => "Loading countries..."}
                                />
                              )}
                            />
                            {profileErrors.country && (
                              <span className="error-text">
                                {profileErrors.country.message}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              Email Address
                              <BiEdit
                                style={{
                                  fontSize: "16px",
                                  color: "#007bff",
                                  cursor: "pointer",
                                }}
                                onClick={() => setShowEmailModal(true)}
                                title="Change Email"
                              />
                            </label>
                            <input
                              value={profileData?.email || ""}
                              className="form-input"
                              placeholder="Email address"
                              readOnly
                              style={{
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div
                            className="form-group"
                            style={{ position: "relative" }}
                          >
                            <label>Contact Number</label>
                            <div style={{ position: "relative" }}>
                              {selectedCountryCode && (
                                <div
                                  className="position-absolute d-flex align-items-center"
                                  style={{
                                    left: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    zIndex: 0,
                                    borderRight: "1px solid #ddd",
                                    paddingRight: "8px",
                                    height: "24px",
                                    gap: "4px",
                                    background: "#fff",
                                  }}
                                >
                                  <img
                                    src={`https://flagcdn.com/${flagLink?.toLowerCase()}.svg`}
                                    alt="flag"
                                    width={20}
                                    height={14}
                                    style={{ objectFit: "cover" }}
                                    onError={(e) => {
                                      e.target.src =
                                        "https://flagcdn.com/us.svg";
                                    }}
                                  />
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    ({countryCode})
                                  </span>
                                </div>
                              )}
                              <input
                                {...registerProfile("contactNumber")}
                                type="tel"
                                className="form-input"
                                placeholder="Enter contact number"
                                style={{
                                  paddingLeft: selectedCountryCode
                                    ? "85px"
                                    : "12px",
                                  height: "30px",
                                  width: "100%",
                                }}
                              />
                            </div>
                            {profileErrors.contactNumber && (
                              <span className="error-text">
                                {profileErrors.contactNumber.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4 mb-3">
                          <div className="form-group">
                            <label>Button Color</label>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="color"
                                value={watchProfile("buttonColor") || "#737EE2"}
                                onChange={(e) =>
                                  setProfileValue("buttonColor", e.target.value)
                                }
                                style={{
                                  width: "50px",
                                  height: "35px",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              />
                              <input
                                {...registerProfile("buttonColor")}
                                className="form-input"
                                placeholder="#737EE2"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-4 mb-3">
                          <div className="form-group">
                            <label>Background Color</label>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="color"
                                value={
                                  watchProfile("backgroundColor") || "#FFFFFF"
                                }
                                onChange={(e) =>
                                  setProfileValue(
                                    "backgroundColor",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  width: "50px",
                                  height: "35px",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              />
                              <input
                                {...registerProfile("backgroundColor")}
                                className="form-input"
                                placeholder="#FFFFFF"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-4 mb-3 d-flex align-items-end">
                          <button
                            type="button"
                            className="add-btn"
                            onClick={handlePreviewClick}
                            disabled={!id}
                          >
                            Preview Branding
                          </button>
                        </div>
                      </div>
                      <div className="form-actions mt-3">
                        <button
                          type="submit"
                          className="add-btn"
                          disabled={isProfileSubmitting}
                        >
                          {isProfileSubmitting
                            ? "Updating..."
                            : "Update Profile"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="page-card1 p-4">
                  <div>
                    <h6
                      style={{
                        borderBottom: "1px solid #ddd",
                        paddingBottom: "10px",
                        marginBottom: "20px",
                      }}
                    >
                      Change Password
                    </h6>
                    <form
                      onSubmit={handlePasswordSubmit(onPasswordSubmit)}
                      className="input-form"
                    >
                      <div className="row">
                        <div className="col-12 mb-3">
                          <div className="form-group position-relative">
                            <label>
                              Current Password{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type={showCurrentPassword ? "text" : "password"}
                              {...registerPassword("currentPassword", {
                                required: "Current password is required",
                              })}
                              className="form-input"
                              placeholder="Enter current password"
                            />
                            <div
                              className="position-absolute cursor-pointer"
                              style={{
                                bottom: passwordErrors.currentPassword
                                  ? "30px"
                                  : "8px",
                                right: "13px",
                                color: "#666",
                              }}
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                            >
                              {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                            </div>
                            {passwordErrors.currentPassword && (
                              <span className="error-text">
                                {passwordErrors.currentPassword.message}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-6 mb-3">
                          <div className="form-group position-relative">
                            <label>
                              New Password{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type={showNewPassword ? "text" : "password"}
                              {...registerPassword("newPassword", {
                                required: "New password is required",
                                minLength: {
                                  value: 8,
                                  message:
                                    "Password must be at least 8 characters",
                                },
                              })}
                              className="form-input"
                              placeholder="Enter new password"
                            />
                            <div
                              className="position-absolute cursor-pointer"
                              style={{
                                bottom: passwordErrors.newPassword
                                  ? "30px"
                                  : "8px",
                                right: "13px",
                                color: "#666",
                              }}
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                            >
                              {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                            </div>
                            {passwordErrors.newPassword && (
                              <span className="error-text">
                                {passwordErrors.newPassword.message}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-6 mb-3">
                          <div className="form-group position-relative">
                            <label>
                              Confirm Password{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              {...registerPassword("confirmPassword", {
                                required: "Confirm password is required",
                                validate: {
                                  match: (value) =>
                                    value === newPassword ||
                                    "Passwords don't match",
                                },
                              })}
                              className="form-input"
                              placeholder="Confirm new password"
                            />
                            <div
                              className="position-absolute cursor-pointer"
                              style={{
                                bottom: passwordErrors.confirmPassword
                                  ? "30px"
                                  : "8px",
                                right: "13px",
                                color: "#666",
                              }}
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </div>
                            {passwordErrors.confirmPassword && (
                              <span className="error-text">
                                {passwordErrors.confirmPassword.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="form-actions mt-3">
                        <button
                          type="submit"
                          className="add-btn"
                          disabled={isPasswordSubmitting}
                        >
                          {isPasswordSubmitting
                            ? "Changing..."
                            : "Change Password"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ChangeMailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        currentEmail={profileData?.email}
        oemId={id}
      />

      {showDescriptionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowDescriptionModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "500px",
              height: "250px",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 15px",
                borderBottom: "1px solid #ddd",
              }}
            >
              <h5 style={{ margin: 0, color: "#2c3e50" }}>
                Almata Share Description
              </h5>
              <span
                onClick={() => setShowDescriptionModal(false)}
                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#666",
                  fontWeight: "bold",
                }}
              >
                ×
              </span>
            </div>
            <div style={{ padding: "15px", overflow: "auto", flex: 1 }}>
              <p style={{ color: "#2c3e50", lineHeight: "1.5", margin: 0 }}>
                {profileData?.almataSharedDiscription ||
                  "No description available"}
              </p>
            </div>
          </div>
        </div>
      )}
      {showPreview && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
          }}
          onClick={closePreview}
        >
          <div
            style={{
              width: "95%",
              // maxWidth: "1200px",
              height: "95%",
              backgroundColor: watchProfile("backgroundColor") || "#fff",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closePreview}
              style={{
                position: "absolute",
                top: "12px",
                right: "16px",
                border: "none",
                background: "transparent",
                fontSize: "24px",
                cursor: "pointer",
                color: "#6b7280",
                zIndex: 10,
              }}
            >
              ×
            </button>

            {/* Branding Header */}
            <div
              style={{
                background: watchProfile("backgroundColor") || "#fff",
                textAlign: "center",
              }}
            >
              {profileData?.logo ? (
                <img
                  className="mt-4"
                  src={profileData.logo}
                  alt="Company Logo"
                  style={{
                    height: "60px",
                    width: "auto",
                    maxWidth: "180px",
                    objectFit: "contain",
                    marginBottom: "12px",
                  }}
                />
              ) : null}
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "14px",
                  color: "#6b7280",
                }}
              >
                Cybersecurity Assessment Platform
              </p>
              <p
                style={{
                  margin: "12px 0 0 0",
                  fontSize: "13px",
                  color: "#4b5563",
                  lineHeight: "1.5",
                  maxWidth: "600px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Comprehensive security assessment combining network discovery,
                vulnerability scanning, technology stack analysis, web security
                evaluation, infrastructure assessment, and data breach
                monitoring with AI-powered insights.
              </p>
            </div>

            {/* Preview Content - Matching MainForm exactly */}
            <div style={{ overflowY: "auto", flex: 1, padding: "32px 24px" }}>
              <div
                className="shadow p-4 rounded-4"
                style={{
                  maxWidth: "1200px",
                  margin: "0 auto",
                  background: lightenColor(
                    watchProfile("backgroundColor") || "#ec0a0a",
                    85,
                  ),
                }}
              >
                {/* Header - Matching MainForm exactly */}
                <div style={{ textAlign: "center", marginBottom: "24px" }}>
                  <h4
                    style={{
                      fontWeight: "bold",
                      margin: 0,
                      fontSize: "inherit",
                    }}
                  >
                    Security Assessment Request
                  </h4>
                  <p
                    style={{
                      color: "#6b7280",
                      marginBottom: 0,
                      marginTop: "12px",
                      fontSize: "14px",
                    }}
                  >
                    Request a comprehensive cybersecurity assessment for your
                    domain.
                  </p>
                </div>

                {/* Form Container */}
                <div
                  style={{
                    marginBottom: "20px",
                  }}
                >
                  {/* First/Last Name Row */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginBottom: "6px",
                          display: "block",
                          color: "#111827",
                        }}
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        placeholder="First Name"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                          boxShadow: "none",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginBottom: "6px",
                          display: "block",
                          color: "#111827",
                        }}
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        placeholder="Last Name"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                          boxShadow: "none",
                        }}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: "12px" }}>
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        marginBottom: "6px",
                        display: "block",
                        color: "#111827",
                      }}
                    >
                      Email Address *{" "}
                      <span
                        style={{
                          color: "#6b7280",
                          fontSize: "13px",
                          fontWeight: "400",
                        }}
                      >
                        (Cyber Risk Report will be delivered to this email
                        address)
                      </span>
                    </label>
                    <input
                      type="email"
                      placeholder="Email Address"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "14px",
                        boxSizing: "border-box",
                        boxShadow: "none",
                      }}
                    />
                  </div>

                  {/* Domains Section */}
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "6px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          margin: 0,
                          color: "#111827",
                        }}
                      >
                        Domain to Assess *
                      </label>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <span
                        style={{
                          padding: "8px 12px",
                          backgroundColor: "#f3f4f6",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        https://
                      </span>
                      <input
                        type="text"
                        placeholder="example.com"
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          boxSizing: "border-box",
                          boxShadow: "none",
                        }}
                      />
                    </div>
                    <small
                      style={{
                        color: "#6b7280",
                        display: "block",
                        marginTop: "4px",
                        fontSize: "12px",
                      }}
                    >
                      Enter just the domain (e.g., example.com). Your email
                      domain must match.
                    </small>
                  </div>
                </div>

                {/* Terms Checkbox */}
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{
                        marginTop: "4px",
                        cursor: "pointer",
                        boxShadow: "none",
                      }}
                      id="preview-tos"
                    />
                    <label
                      htmlFor="preview-tos"
                      style={{
                        fontSize: "13px",
                        color: "#374151",
                        margin: 0,
                        cursor: "pointer",
                      }}
                    >
                      I agree to understand that I may only assess domains I own
                      or have explicit permission to scan.
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    background: `linear-gradient(to right, ${watchProfile("buttonColor") || "#2563eb"}, ${watchProfile("buttonColor") || "#4338ca"})`,
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "16px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <i style={{ display: "inline" }}>🔒</i>
                  Start Security Assessment
                </button>

                {/* Version Footer */}
                <div
                  className="alert d-flex justify-content-end align-items-center rounded-2 mt-3 px-3 py-1"
                  style={{
                    backgroundColor: "rgb(239 246 255)",
                    border: "1px solid rgb(191 219 254)",
                  }}
                >
                  <p className="mb-0 version">
                    {dbVersion ? `v${dbVersion}` : `v${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;
